import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import { useProperty } from '../context/PropertyContext';
import axios from 'axios';
import '../styles/BookingsList.css';
import DesktopLayout from '../layouts/DesktopLayout';
import { formatShortDate } from '../utils/dateFormatter';
import { formatCount } from '../utils/numberFormatter';
import { arabicPlural } from '../utils/alertDisplay';
import { isOwnerRole } from '../config/roleConfig';
import { useBookingsPaged } from '../hooks/useBookings';
import useLanguage from '../hooks/useLanguage';
import AccountSwitcher from '../components/common/AccountSwitcher';
import PropertySwitcher from '../components/common/PropertySwitcher';
import { getPlatformLogo, getPlatformDisplayName, getPlatformIcon } from '../utils/platformUtils';
import { DATE_RANGE_OPTIONS, DATE_RANGE_ALL, DATE_RANGE_CUSTOM, resolveDateRange } from '../utils/bookingDateRange';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_STATS = { total: 0, upcoming: 0, active: 0, completed: 0, cancelled: 0 };

// Nights between two business dates — shared by the table row and the phone card. Rounded, not
// ceiled: check-in and check-out can be stored on different midnights (UTC vs Riyadh — see
// TEMPORAL_FIELD_CATALOGUE.md), and the 3-hour gap must not add a night.
const getNights = (booking) =>
  Math.round((new Date(booking.checkOut) - new Date(booking.checkIn)) / (1000 * 60 * 60 * 24));

const getConfirmationCode = (booking) =>
  booking.confirmationCode ||
  booking.enrichedData?.confirmationCode ||
  (booking.sourcePlatform === 'direct' ? `HM-${booking._id.slice(-8).toUpperCase()}` : 'غير متوفر');

const BookingsList = () => {
  const { user } = useContext(AuthContext);
  const { t, language } = useLanguage();
  // Owners get the phone card view + collapsible filters (tasks/owner-screens-mobile.md D1/D2);
  // staff keep the sideways-scrolling operational table.
  const isOwner = isOwnerRole(user?.role);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { selectedAccount } = useAccount(); // Global account selection from Context
  const { selectedProperty, setSelectedProperty } = useProperty(); // Global property selection from Context

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    platform: '',
    dateRange: DATE_RANGE_ALL,
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  // Properties for filter dropdown
  const [properties, setProperties] = useState([]);

  // Navigation state
  const [activeNavItem, setActiveNavItem] = useState('bookings');

  // Search hits the server, so wait for the typing to pause.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // The window is picked as Riyadh calendar days; the server turns them into boundaries.
  const dayRange = resolveDateRange(filters.dateRange, { from: filters.dateFrom, to: filters.dateTo });

  // Every filter runs on the server, one page at a time (tasks/owner-screens-mobile.md D3).
  const {
    bookings,
    total,
    stats: serverStats,
    isLoading: loading,
    error: queryError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError
  } = useBookingsPaged({
    propertyId: selectedProperty || undefined,
    status: filters.status || undefined,
    ownerId: selectedAccount || undefined,
    platform: filters.platform || undefined,
    search: debouncedSearch || undefined,
    checkInDayFrom: dayRange?.from,
    checkInDayTo: dayRange?.to
  }, PAGE_SIZE);
  const stats = serverStats || EMPTY_STATS;
  // «ليلة واحدة» / «ليلتان» / «3 ليالٍ» / «11 ليلة» — the same agreement as the owner push.
  const nightsLabel = (n) => (n === 1 ? t('bookingsList.nightOne')
    : n === 2 ? t('bookingsList.nightTwo')
      : `${formatCount(n, language)} ${arabicPlural(n, t('bookingsList.nightMany'), t('bookingsList.nightTwo'), t('bookingsList.nightFew'))}`);
  const activeFilterCount = [filters.status, filters.platform, filters.search.trim(), dayRange]
    .filter(Boolean).length;

  // Convert React Query error to string
  const error = queryError ? 'حدث خطأ في جلب الحجوزات' : null;

  // Fetch properties on component mount and when account changes
  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // Validate selectedProperty exists in properties array (prevent stale selection)
  useEffect(() => {
    if (selectedProperty && properties.length > 0) {
      const exists = properties.some(p => String(p._id) === String(selectedProperty));
      if (!exists) {
        setSelectedProperty(null); // Reset to "All Properties"
      }
    }
  }, [properties, selectedProperty, setSelectedProperty]);

  // Clear selectedProperty when admin switches accounts (prevent context pollution)
  useEffect(() => {
    if ((user?.role === 'admin' || user?.role === 'ops-manager') && selectedAccount) {
      if (selectedProperty) {
        setSelectedProperty(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  const fetchProperties = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${API_BASE}/api/v1/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle multiple response formats (matching Revenue Dashboard pattern)
      let userProperties = [];
      if (Array.isArray(response.data)) {
        userProperties = response.data;
      } else if (response.data && response.data.properties) {
        userProperties = response.data.properties;
      } else if (response.data && response.data.data) {
        userProperties = response.data.data;
      }

      // Filter by selected account (for admin/ops only)
      if (selectedAccount && selectedAccount !== 'all') {
        userProperties = userProperties.filter(property => {
          // Check if the selected account is in the owners array
          const hasOwner = property.owners && property.owners.some(owner =>
            owner.userId && (
              owner.userId._id === selectedAccount ||
              owner.userId === selectedAccount
            )
          );
          return hasOwner;
        });
      }

      setProperties(userProperties);
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  }, [selectedAccount]);

  // Using centralized date formatter utility (formatShortDate from dateFormatter.js)

  const getStatusColor = (status) => {
    const colors = {
      upcoming: '#17a2b8',
      active: '#28a745',
      completed: '#6c757d',
      cancelled: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusText = (status) => {
    const texts = {
      upcoming: 'حجز مؤكد',
      active: 'حجز نشط',
      completed: 'مكتمل',
      cancelled: 'ملغي'
    };
    return texts[status] || status;
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      platform: '',
      dateRange: DATE_RANGE_ALL,
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  if (loading && bookings.length === 0) {
    return (
      <DesktopLayout 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="إدارة الحجوزات"
      >
        <div className="bookings-loading" dir="rtl">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p>جاري تحميل الحجوزات...</p>
        </div>
      </DesktopLayout>
    );
  }

  if (error && bookings.length === 0) {
    return (
      <DesktopLayout 
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="إدارة الحجوزات"
      >
        <div className="bookings-error" dir="rtl">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button onClick={refetch} className="btn btn-primary">
            إعادة المحاولة
          </button>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout 
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="إدارة الحجوزات"
    >
      <div className={`bookings-list${isOwner ? ' owner-view' : ''}${filtersOpen ? ' filters-open' : ''}`} dir="rtl">
      {/* Header actions */}
      <div className="header-actions">
        <Link to="/dashboard" className="btn btn-secondary touch-btn">
          <i className="bi bi-arrow-right"></i> العودة إلى لوحة التحكم
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="bookings-stats">
        <div className="stat-card total touch-card">
          <div className="stat-icon">
            <i className="bi bi-calendar3"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>إجمالي الحجوزات</p>
          </div>
        </div>

        <div className="stat-card upcoming touch-card">
          <div className="stat-icon">
            <i className="bi bi-clock"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.upcoming}</h3>
            <p>حجوزات مؤكدة</p>
          </div>
        </div>

        <div className="stat-card active touch-card">
          <div className="stat-icon">
            <i className="bi bi-house-check"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.active}</h3>
            <p>حجوزات نشطة</p>
          </div>
        </div>

        <div className="stat-card completed touch-card">
          <div className="stat-icon">
            <i className="bi bi-check-circle"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.completed}</h3>
            <p>حجوزات مكتملة</p>
          </div>
        </div>
      </div>

      {/* Account/Property Switcher - Role-based */}
      <div className="switcher-container" style={{ marginBottom: '1.5rem' }}>
        {(user?.role === 'admin' || user?.role === 'ops-manager') ? (
          // Admin/Ops: Show Account Switcher (reads/writes to Context)
          <AccountSwitcher />
        ) : (
          // Property Owners: Show Property Switcher (reads/writes to Context)
          <PropertySwitcher properties={properties} isLoading={loading && properties.length === 0} />
        )}
      </div>

      {/* Phone (owners): filters fold behind one button so the list starts on screen one */}
      <button
        type="button"
        className="btn btn-outline-primary touch-btn bookings-filters-toggle"
        onClick={() => setFiltersOpen(open => !open)}
        aria-expanded={filtersOpen}
      >
        <i className="bi bi-funnel"></i> {t('lists.filtersToggle')}
        {activeFilterCount > 0 && <span className="filters-toggle-count">{activeFilterCount}</span>}
      </button>

      {/* Filters */}
      <div className="bookings-filters">
        <h5>تصفية الحجوزات</h5>
        <div className="filters-grid">
          <div className="filter-group">
            <label>البحث</label>
            <input
              type="text"
              placeholder="بحث بالاسم، العقار، أو رقم التأكيد"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="form-control touch-input"
            />
          </div>
          
          <div className="filter-group">
            <label>الحالة</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="form-control touch-select"
            >
              <option value="">جميع الحالات</option>
              <option value="upcoming">حجز مؤكد</option>
              <option value="active">نشط</option>
              <option value="completed">مكتمل</option>
              <option value="cancelled">ملغي</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>المنصة</label>
            <select
              value={filters.platform}
              onChange={(e) => setFilters({...filters, platform: e.target.value})}
              className="form-control touch-select"
            >
              <option value="">جميع المنصات</option>
              <option value="airbnb">Airbnb</option>
              <option value="booking">Booking.com</option>
              <option value="hostme">Host Me</option>
              <option value="gathern">GatherN</option>
            </select>
          </div>

          <div className="filter-group">
            <label>الفترة الزمنية</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              className="form-control touch-select"
            >
              {DATE_RANGE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {filters.dateRange === DATE_RANGE_CUSTOM && (
            <>
              <div className="filter-group">
                <label>من</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  max={filters.dateTo || undefined}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="form-control touch-input"
                />
              </div>

              <div className="filter-group">
                <label>إلى</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  min={filters.dateFrom || undefined}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="form-control touch-input"
                />
              </div>
            </>
          )}
        </div>
        
        <div className="filter-actions">
          <button onClick={resetFilters} className="btn btn-outline-secondary touch-btn">
            <i className="bi bi-x-circle"></i> إعادة تعيين
          </button>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bookings-table-section">
        <div className="section-header">
          <h2>قائمة الحجوزات</h2>
          <span className="bookings-count">
            {t('lists.showingOf').replace('{shown}', bookings.length).replace('{total}', total)}
          </span>
        </div>
        
        <div className="bookings-table-wrapper">
          <table className="bookings-table">
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>المدة</th>
                <th>رقم الحجز</th>
                <th>العقار</th>
                <th>الضيف</th>
                <th>المنصة</th>
                <th>المبلغ</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={9} className="no-data">
                    {activeFilterCount > 0 ? t('bookingsList.noMatches') : t('bookingsList.noBookings')}
                  </td>
                </tr>
              ) : (
                bookings.map(booking => (
                  <tr key={booking._id}>
                    <td>
                      <div className="date-cell">
                        <div>{formatShortDate(booking.checkIn)}</div>
                        <small className="text-muted">
                          {formatShortDate(booking.checkOut)}
                        </small>
                      </div>
                    </td>
                    <td>
                      {getNights(booking)} ليلة
                    </td>
                    <td>
                      {getConfirmationCode(booking)}
                    </td>
                    <td>
                      <Link
                        to={`/properties/${booking.property._id}`}
                        className="property-link"
                        title={booking.property.ownerName ? `المالك: ${booking.property.ownerName}` : 'المالك غير محدد'}
                      >
                        {booking.property.name}
                        {booking.property.propertyCode && (
                          <span className="text-muted ms-2" dir="ltr" style={{ fontSize: '0.8em' }}>
                            {booking.property.propertyCode}
                          </span>
                        )}
                      </Link>
                    </td>
                    <td>{booking.guestName || 'غير محدد'}</td>
                    <td>
                      <div className="platform-cell">
                        {getPlatformLogo(booking.sourcePlatform) ? (
                          <img
                            src={getPlatformLogo(booking.sourcePlatform)}
                            alt={booking.sourcePlatform}
                            className="platform-logo"
                            title={getPlatformDisplayName(booking.sourcePlatform)}
                          />
                        ) : (
                          <span className="platform-badge-text">
                            <i className={`bi ${getPlatformIcon(booking.sourcePlatform)}`}></i>
                            {getPlatformDisplayName(booking.sourcePlatform)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="amount">
                      {booking.totalAmount ? `${booking.totalAmount} ${booking.currency || 'SAR'}` : 'غير محدد'}
                    </td>
                    <td>
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(booking.status) }}
                      >
                        {getStatusText(booking.status)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/bookings/${booking._id}`}
                          className="btn btn-sm btn-outline-primary"
                          title="عرض التفاصيل"
                        >
                          <i className="bi bi-eye"></i>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Phone card view — owners only; CSS shows it below 768px instead of the table */}
        {isOwner && (
          <div className="bookings-cards">
            {bookings.length === 0 ? (
              <div className="no-data">{activeFilterCount > 0 ? t('bookingsList.noMatches') : t('bookingsList.noBookings')}</div>
            ) : (
              bookings.map(booking => (
                <Link key={booking._id} to={`/bookings/${booking._id}`} className="booking-card touch-card">
                  <div className="booking-card-head">
                    <span className="booking-card-unit">
                      {booking.property?.name}
                      {booking.property?.propertyCode && (
                        <span className="booking-card-code" dir="ltr">{booking.property.propertyCode}</span>
                      )}
                    </span>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(booking.status) }}>
                      {getStatusText(booking.status)}
                    </span>
                  </div>
                  <div className="booking-card-dates">
                    {formatShortDate(booking.checkIn)} ← {formatShortDate(booking.checkOut)}
                    <span className="booking-card-nights">· {nightsLabel(getNights(booking))}</span>
                  </div>
                  <div className="booking-card-meta">
                    <span className="booking-card-guest">{booking.guestName || t('bookingsList.notSet')}</span>
                    <span className="booking-card-platform">
                      {getPlatformLogo(booking.sourcePlatform) ? (
                        <img
                          src={getPlatformLogo(booking.sourcePlatform)}
                          alt={getPlatformDisplayName(booking.sourcePlatform)}
                          className="platform-logo"
                        />
                      ) : (
                        getPlatformDisplayName(booking.sourcePlatform)
                      )}
                    </span>
                    <span className="booking-card-amount">
                      {booking.totalAmount ? `${booking.totalAmount} ${booking.currency || 'SAR'}` : t('bookingsList.notSet')}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        )}

        {hasNextPage && (
          <div className="list-load-more">
            <button
              type="button"
              className="btn btn-outline-primary touch-btn"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? t('lists.loadingMore') : t('lists.loadMore')}
            </button>
            {isFetchNextPageError && !isFetchingNextPage && (
              <span className="text-danger" role="alert" style={{ marginInlineStart: '12px' }}>
                {t('lists.loadMoreFailed')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
    </DesktopLayout>
  );
};

export default BookingsList;
